import type { Body, Joint } from "../../types/scene";
import { findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { logger } from "../../utils/logger";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import type { ExportActuator, JointInfo, TendonCoefficient } from "./xmlGenerationTypes";

export type TendonPairForExport = {
  joint1: string;
  joint2: string;
  tendonName: string;
  actuator?: ExportActuator | null;
};

export const buildJointInfoMaps = ({
  bodiesMap,
  bodyRootMap,
  joints,
}: {
  bodiesMap: Record<string, Body>;
  bodyRootMap: Record<string, string>;
  joints: Joint[];
}): {
  jointBySanitizedName: Record<string, JointInfo>;
  jointInfoByUuid: Map<string, JointInfo>;
} => {
  const jointBySanitizedName: Record<string, JointInfo> = {};
  const jointInfoByUuid = new Map<string, JointInfo>();
  joints.forEach((j) => {
    const sanitized = sanitizeMjcfName(j.name);
    const hostBodyUuid = findBodyForJointUuid(bodiesMap, j.uuid);
    const hostBody = hostBodyUuid ? bodiesMap[hostBodyUuid] : null;
    const rootUuid = hostBodyUuid ? bodyRootMap[hostBodyUuid] || hostBodyUuid : null;
    const info: JointInfo = {
      uuid: j.uuid,
      name: j.name,
      bodyName: hostBody?.name || null,
      bodyUuid: hostBodyUuid || null,
      rootUuid,
      kind: j.kind,
    };
    jointBySanitizedName[sanitized] = info;
    jointInfoByUuid.set(j.uuid, info);
  });
  return { jointBySanitizedName, jointInfoByUuid };
};

export const normalizeActuators = (
  sceneActuators: Record<string, ExportActuator> | ExportActuator[] | undefined
): ExportActuator[] =>
  Array.isArray(sceneActuators)
    ? sceneActuators
    : sceneActuators && typeof sceneActuators === "object"
      ? Object.values(sceneActuators)
      : [];

export const buildTendonPairs = ({
  equality,
  jointMap,
  jointBySanitizedName,
  tendonCoefficients,
  tendonActuators,
}: {
  equality: Record<string, { type?: string; objtype?: string; obj1?: string; obj2?: string }>;
  jointMap: Record<string, Joint>;
  jointBySanitizedName: Record<string, JointInfo>;
  tendonCoefficients: Map<string, TendonCoefficient[]>;
  tendonActuators: ExportActuator[];
}): TendonPairForExport[] => {
  const tendonPairs: TendonPairForExport[] = [];
  const emittedTendonPairNames = new Set<string>();
  let tendonPairCounter = 0;
  try {
    const eqList = Object.values(equality);
    eqList.forEach((eq) => {
      if (!eq || eq.type !== "joint" || (eq.objtype !== "joint" && eq.objtype)) return;

      const j1Uuid = eq.obj1 as import("../../types/scene").JointUUID;
      const j2Uuid = eq.obj2 as import("../../types/scene").JointUUID;
      if (!j1Uuid || !j2Uuid) return;
      const joint1 = jointMap[j1Uuid];
      const joint2 = jointMap[j2Uuid];
      if (!joint1 || !joint2) return;
      const j1 = joint1.name;
      const j2 = joint2.name;
      if (!j1 || !j2) return;
      const s1 = sanitizeMjcfName(j1);
      const s2 = sanitizeMjcfName(j2);
      const info1 = jointBySanitizedName[s1];
      const info2 = jointBySanitizedName[s2];
      if (!info1 || !info2) return;
      const kind1 = String(info1.kind || "").toLowerCase();
      const kind2 = String(info2.kind || "").toLowerCase();
      const dof1Ok = kind1 === "slide" || kind1 === "hinge" || kind1 === "";
      const dof2Ok = kind2 === "slide" || kind2 === "hinge" || kind2 === "";
      if (!dof1Ok || !dof2Ok) return;

      let matchedTendonName: string | null = null;
      let matchedActuator: ExportActuator | null = null;

      for (const [tendonName, jointCoefs] of tendonCoefficients.entries()) {
        const tendonJointNames = jointCoefs.map((jc) => sanitizeMjcfName(jc.joint));
        const matchesJ1 = tendonJointNames.some((name) => name === s1);
        const matchesJ2 = tendonJointNames.some((name) => name === s2);
        if (matchesJ1 && matchesJ2) {
          matchedTendonName = tendonName;
          matchedActuator =
            tendonActuators.find(
              (a) => sanitizeMjcfName(a.tendon || "") === sanitizeMjcfName(tendonName)
            ) ?? null;
          break;
        }
      }

      if (!matchedTendonName && tendonActuators.length > tendonPairCounter) {
        matchedActuator = tendonActuators[tendonPairCounter] ?? null;
        matchedTendonName = matchedActuator?.tendon ?? null;
      }

      if (!matchedTendonName || matchedActuator?.tendonUuid) return;
      if (emittedTendonPairNames.has(matchedTendonName)) return;

      emittedTendonPairNames.add(matchedTendonName);
      tendonPairs.push({
        joint1: s1,
        joint2: s2,
        tendonName: matchedTendonName,
        actuator: matchedActuator,
      });
      tendonPairCounter++;
    });
  } catch (error) {
    logger.warn("[FileStore] Error detecting tendon pairs:", error);
  }
  return tendonPairs;
};

export const buildTendonActuatorsByName = (
  actuatorsArray: ExportActuator[]
): Map<string, ExportActuator[]> => {
  const tendonActuatorsByName = new Map<string, ExportActuator[]>();
  actuatorsArray.forEach((actuator) => {
    if (!actuator?.tendon) return;
    const tName = actuator.tendon;
    if (!tendonActuatorsByName.has(tName)) tendonActuatorsByName.set(tName, []);
    tendonActuatorsByName.get(tName)!.push(actuator);
  });
  return tendonActuatorsByName;
};
